import { useState } from 'react';
import {
  IconSearch,
  IconPlus,
  IconEye,
  IconUser,
} from '@tabler/icons-react';
import { ActionIcon, Avatar, Badge, Box, Button, Card, Group, Select, Stack, Table, Text, TextInput, Title } from '@/components/ui';
import { mockTickets, ticketCategories, Ticket } from '../mockData/helpdesk';

const formatDateTime = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const getStatusColor = (status: Ticket['status']) => {
  switch (status) {
    case 'new': return 'blue';
    case 'open': return 'cyan';
    case 'in-progress': return 'yellow';
    case 'pending': return 'orange';
    case 'resolved': return 'green';
    case 'closed': return 'gray';
    default: return 'gray';
  }
};

const getPriorityColor = (priority: Ticket['priority']) => {
  switch (priority) {
    case 'urgent': return 'red';
    case 'high': return 'orange';
    case 'medium': return 'yellow';
    case 'low': return 'green';
    default: return 'gray';
  }
};

export default function TicketsPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [priorityFilter, setPriorityFilter] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

  const statuses = ['new', 'open', 'in-progress', 'pending', 'resolved', 'closed'];
  const priorities = ['urgent', 'high', 'medium', 'low'];

  const filteredTickets = mockTickets.filter(ticket => {
    const matchesSearch = ticket.subject.toLowerCase().includes(search.toLowerCase()) ||
                          ticket.ticketNumber.toLowerCase().includes(search.toLowerCase()) ||
                          ticket.customerName.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = !statusFilter || ticket.status === statusFilter;
    const matchesPriority = !priorityFilter || ticket.priority === priorityFilter;
    const matchesCategory = !categoryFilter || ticket.category === categoryFilter;
    return matchesSearch && matchesStatus && matchesPriority && matchesCategory;
  });

  // Sort by created date descending
  const sortedTickets = [...filteredTickets].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>Support Tickets</Title>
          <Text c="dimmed" size="sm">{filteredTickets.length} tickets</Text>
        </Box>
        <Button leftSection={<IconPlus size={16} />}>New Ticket</Button>
      </Group>

      {/* Summary Cards */}
      <Group>
        <Card style={{ flex: 1 }}>
          <Text size="xs" c="dimmed">New</Text>
          <Text size="xl" fw={700} c="blue">{mockTickets.filter(t => t.status === 'new').length}</Text>
        </Card>
        <Card style={{ flex: 1 }}>
          <Text size="xs" c="dimmed">In Progress</Text>
          <Text size="xl" fw={700} c="yellow">{mockTickets.filter(t => t.status === 'in-progress').length}</Text>
        </Card>
        <Card style={{ flex: 1 }}>
          <Text size="xs" c="dimmed">Pending</Text>
          <Text size="xl" fw={700} c="orange">{mockTickets.filter(t => t.status === 'pending').length}</Text>
        </Card>
        <Card style={{ flex: 1 }}>
          <Text size="xs" c="dimmed">Resolved</Text>
          <Text size="xl" fw={700} c="green">{mockTickets.filter(t => ['resolved', 'closed'].includes(t.status)).length}</Text>
        </Card>
      </Group>

      {/* Filters */}
      <Card>
        <Group>
          <TextInput
            placeholder="Search tickets..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Status"
            data={statuses.map(s => ({ value: s, label: s.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') }))}
            value={statusFilter}
            onChange={setStatusFilter}
            clearable
            w={150}
          />
          <Select
            placeholder="Priority"
            data={priorities.map(p => ({ value: p, label: p.charAt(0).toUpperCase() + p.slice(1) }))}
            value={priorityFilter}
            onChange={setPriorityFilter}
            clearable
            w={120}
          />
          <Select
            placeholder="Category"
            data={ticketCategories.map(c => ({ value: c, label: c }))}
            value={categoryFilter}
            onChange={setCategoryFilter}
            clearable
            w={180}
          />
        </Group>
      </Card>

      {/* Tickets Table */}
      <Card>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Ticket</Table.Th>
              <Table.Th>Customer</Table.Th>
              <Table.Th>Category</Table.Th>
              <Table.Th>Priority</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Assignee</Table.Th>
              <Table.Th>Created</Table.Th>
              <Table.Th w={60}></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sortedTickets.map(ticket => (
              <Table.Tr key={ticket.id}>
                <Table.Td>
                  <Box>
                    <Text size="sm" fw={500} lineClamp={1}>{ticket.subject}</Text>
                    <Text size="xs" c="dimmed">{ticket.ticketNumber}</Text>
                  </Box>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <Avatar size="sm" radius="xl" color="blue">
                      {ticket.customerName.split(' ').map(n => n[0]).join('')}
                    </Avatar>
                    <Box>
                      <Text size="sm">{ticket.customerName}</Text>
                      <Text size="xs" c="dimmed">{ticket.customerEmail}</Text>
                    </Box>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Badge size="sm" variant="light">{ticket.category}</Badge>
                </Table.Td>
                <Table.Td>
                  <Badge size="sm" color={getPriorityColor(ticket.priority)}>{ticket.priority}</Badge>
                </Table.Td>
                <Table.Td>
                  <Badge size="sm" color={getStatusColor(ticket.status)}>
                    {ticket.status.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  {ticket.assigneeName ? (
                    <Group gap="xs">
                      <Avatar size="xs" radius="xl" color="violet">
                        {ticket.assigneeName.split(' ').map(n => n[0]).join('')}
                      </Avatar>
                      <Text size="sm">{ticket.assigneeName}</Text>
                    </Group>
                  ) : (
                    <Group gap="xs">
                      <IconUser size={14} color="var(--mantine-color-dimmed)" />
                      <Text size="sm" c="dimmed">Unassigned</Text>
                    </Group>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">{formatDateTime(ticket.createdAt)}</Text>
                </Table.Td>
                <Table.Td>
                  <ActionIcon variant="light" size="sm">
                    <IconEye size={14} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>
    </Stack>
  );
}
